import { FC, useContext, useMemo } from "react"

import { Typography } from "antd";
import { AnchorLinkItemProps } from "antd/es/anchor/Anchor";

import { Endorsement, InsurancePolicy } from "../../../../../shared/interfaces"
import { Anchor, ColSize, ErrorResults, RowSize, SectionsSkeleton } from "../../../../../shared/components";
import { Column, _renderSection } from "../../utils/renderSections";
import { UIContext } from "../../../../../shared/contexts";
import { formatDate, formattedPercentage, formattedPrice } from "../../../../../shared/utils";

import './index.css'

interface MainTabProps {
    insurancePolicyDetail?: InsurancePolicy;
    selectedEndorsement?: Endorsement;
    showError?: boolean;
}

const MOCK_SECTIONS: Array<AnchorLinkItemProps> = [
    {
        key: 'datos-generales',
        href: '#datos-generales',
        title: 'Datos generales'
    },
    {
        key: 'comisiones',
        href: '#comisiones',
        title: 'Comisiones'
    },
    {
        key: 'premio',
        href: '#premio',
        title: 'Premio'
    },
];

export const MainTab: FC<MainTabProps> = ({ insurancePolicyDetail, selectedEndorsement, showError = false }) => {

    const { isLoading } = useContext( UIContext );

    const generalData: Array<Column> = useMemo(() => [
        {
            label: 'Póliza Nueva',
            value: insurancePolicyDetail?.descripcionRama,
            valueInBold: true,
        },
        {
            label: 'Vigencia de la póliza',
            value: `${ formatDate( selectedEndorsement?.fechaDesde ?? '' ) } - ${ formatDate( selectedEndorsement?.fechaHasta ?? '' )}`,
        },
        {
            label: 'F. Emisión',
            value: formatDate( selectedEndorsement?.fechaEmis ?? ''),
        }
    ], [insurancePolicyDetail, selectedEndorsement]);

    const premioData: Array<Column> = useMemo(() => [
        {
            label: 'Prima Neta',
            value: `${ selectedEndorsement?.moneda } ${formattedPrice(selectedEndorsement?.prima)}`,
        },
        {
            label: 'Sellado',
            value: `${ selectedEndorsement?.moneda } ${ formattedPrice(selectedEndorsement?.sellado) }`,
        },
        {
            label: `Extra Prima Variable (${ formattedPercentage(selectedEndorsement?.porcExtraPaVar) })`,
            value: `${ selectedEndorsement?.moneda } ${ formattedPrice(selectedEndorsement?.extraPaVar) }`,
        },
        {
            label: `IVA. Total (${ formattedPercentage(selectedEndorsement?.porcIva) })`,
            value: `${ selectedEndorsement?.moneda } ${ formattedPrice(selectedEndorsement?.ivaTotal) }`,
        },
        {
            label: `Recargo Financiero (${ formattedPercentage(selectedEndorsement?.porcRecFinanciero) })`,
            value: `${ selectedEndorsement?.moneda } ${ formattedPrice(selectedEndorsement?.recFinanciero) }`,
        },
        {
            label: 'Ingresos Brutos',
            value: `${ selectedEndorsement?.moneda } ${ formattedPrice(selectedEndorsement?.ingresosBrutos) }`,
        },
        {
            label: 'Extra Prima Fija',
            value: `${ selectedEndorsement?.moneda } ${ formattedPrice(selectedEndorsement?.extraPaFija) }`,
        },
        {
            label: `Impuestos (${ formattedPercentage(selectedEndorsement?.porcImpuestos) })`,
            value: `${ selectedEndorsement?.moneda } ${ formattedPrice(selectedEndorsement?.impuestos) }`,
        },
        {
            label: 'Premio',
            value: `${ selectedEndorsement?.moneda } ${ formattedPrice(selectedEndorsement?.premio) }`,
        },
    ], [ selectedEndorsement ]);
    
    
    const generateCommissionsData = (): Array<Column> => {
        let comissionsData: Array<Column> = [];

        selectedEndorsement?.comisiones.map(({ nivel, codigo, porcComision, comision, nombre }) => {
            comissionsData.push({ label: `Nivel ${ nivel }`, value: codigo });
            comissionsData.push({ label: 'Nombre', value: nombre });
            return comissionsData.push({ label: `Comisión ${ formattedPercentage( porcComision ) }`, value: ` ${ selectedEndorsement?.moneda } ${ formattedPrice( comision ) }` });
        })

        return comissionsData;
    };

    return (
        <div className="main-tab-container">
            <RowSize rowProps={{gutter:[0 , 0]}} >
                {
                    isLoading
                        ? (
                            <SectionsSkeleton  />
                        )
                        :  showError 
                            ? <ErrorResults title="Hubo un error intentando cargar los endosos de la póliza" />
                            : (
                                <>            
                                    <ColSize size={ 80 } colProps={{ xs:{span: 20}, sm:{span: 20}}}>
                                        { _renderSection('Datos generales', 'datos-generales', generalData) }
    
                                        { 
                                            _renderSection(
                                                'Comisiones', 
                                                'comisiones', 
                                                generateCommissionsData(),
                                                selectedEndorsement?.comisiones.length
                                                    ? <></>
                                                    : (
                                                        <Typography.Text>
                                                            No existen comisiones para el endoso seleccionado.
                                                        </Typography.Text>
                                                    )
                                            )
                                        }
    
                                        { _renderSection('Premio', 'premio', premioData) }
                                    </ColSize>
    
                                    <ColSize size={ 9 } colProps={{id:"main-tab-anchor-container", xs:{span: 3}, sm:{span: 3}}}>
                                        <Anchor 
                                            items={ MOCK_SECTIONS }
                                            containerId="main-tab-anchor-container"
                                        />
                                    </ColSize>
                                </>
                            )
                }
            </RowSize>
        </div>
    )
}
